/**
 * Persists packaging suppliers list UI state in sessionStorage so breadcrumb
 * and in-flow returns can restore pagination, filters, and search without
 * expanding URL query params.
 *
 * Storage is scoped per organization and best-effort: missing `window`,
 * private mode, quota errors, and disabled storage are handled quietly.
 */

const STORAGE_KEY_PREFIX = 'packaging.suppliers.listState:' as const

export type SuppliersListState = {
  page: number
  pageSize: number
  funnel?: string[]
  redFlags?: string[]
  searchQuery?: string
  sortBy?: string
  sortDir?: 'asc' | 'desc'
}

export const SUPPLIERS_LIST_DEFAULT_PAGE = 1
export const SUPPLIERS_LIST_DEFAULT_PAGE_SIZE = 20

/** Pass on sidebar navigation to open a clean suppliers list. */
export const SUPPLIERS_LIST_NAV_RESET_STATE = {
  resetSuppliersList: true,
} as const

export type SuppliersListLocationState = {
  resetSuppliersList?: boolean
  restoreSuppliersList?: boolean
}

export function isSuppliersListResetNavigation(
  state: unknown
): state is { resetSuppliersList: true } {
  return (
    state !== null &&
    typeof state === 'object' &&
    'resetSuppliersList' in state &&
    state.resetSuppliersList === true
  )
}

export function createDefaultSuppliersListState(): SuppliersListState {
  return {
    page: SUPPLIERS_LIST_DEFAULT_PAGE,
    pageSize: SUPPLIERS_LIST_DEFAULT_PAGE_SIZE,
  }
}

export type ResolvedSuppliersListMount = {
  initialState: SuppliersListState
}

export function resolveSuppliersListMount(
  organizationId: string,
  locationState: unknown
): ResolvedSuppliersListMount {
  if (isSuppliersListResetNavigation(locationState)) {
    clearSuppliersListState(organizationId)
    return {
      initialState: createDefaultSuppliersListState(),
    }
  }

  const stored = readSuppliersListState(organizationId)
  if (stored !== null) {
    return {
      initialState: stored,
    }
  }

  return {
    initialState: createDefaultSuppliersListState(),
  }
}

function getStorage(): Storage | null {
  try {
    if (typeof window === 'undefined') {
      return null
    }
    return window.sessionStorage
  } catch {
    return null
  }
}

function makeKey(organizationId: string): string {
  return `${STORAGE_KEY_PREFIX}${organizationId}`
}

function isStringArray(value: unknown): value is string[] {
  return (
    Array.isArray(value) && value.every((item) => typeof item === 'string')
  )
}

function parseSuppliersListState(raw: string): SuppliersListState | null {
  try {
    const parsed: unknown = JSON.parse(raw)
    if (parsed === null || typeof parsed !== 'object') {
      return null
    }

    const record = parsed as Record<string, unknown>
    const page = record.page
    const pageSize = record.pageSize

    if (
      typeof page !== 'number' ||
      !Number.isInteger(page) ||
      page < 1 ||
      typeof pageSize !== 'number' ||
      !Number.isInteger(pageSize) ||
      pageSize < 1
    ) {
      return null
    }

    const state: SuppliersListState = { page, pageSize }

    if (record.funnel !== undefined) {
      if (!isStringArray(record.funnel)) {
        return null
      }
      state.funnel = record.funnel
    }

    if (record.redFlags !== undefined) {
      if (!isStringArray(record.redFlags)) {
        return null
      }
      state.redFlags = record.redFlags
    }

    if (record.searchQuery !== undefined) {
      if (typeof record.searchQuery !== 'string') {
        return null
      }
      state.searchQuery = record.searchQuery
    }

    if (record.sortBy !== undefined) {
      if (typeof record.sortBy !== 'string') {
        return null
      }
      state.sortBy = record.sortBy
    }

    if (record.sortDir !== undefined) {
      if (record.sortDir !== 'asc' && record.sortDir !== 'desc') {
        return null
      }
      state.sortDir = record.sortDir
    }

    return state
  } catch {
    return null
  }
}

export function saveSuppliersListState(
  organizationId: string,
  state: SuppliersListState
): void {
  const storage = getStorage()
  if (storage === null || organizationId.length === 0) {
    return
  }

  try {
    storage.setItem(makeKey(organizationId), JSON.stringify(state))
  } catch {
    // Quota, private mode, or disabled storage — ignore.
  }
}

export function readSuppliersListState(
  organizationId: string
): SuppliersListState | null {
  const storage = getStorage()
  if (storage === null || organizationId.length === 0) {
    return null
  }

  try {
    const raw = storage.getItem(makeKey(organizationId))
    if (raw === null) {
      return null
    }
    return parseSuppliersListState(raw)
  } catch {
    return null
  }
}

export function clearSuppliersListState(organizationId: string): void {
  const storage = getStorage()
  if (storage === null || organizationId.length === 0) {
    return
  }

  try {
    storage.removeItem(makeKey(organizationId))
  } catch {
    // best-effort cleanup
  }
}

/** Test-only helper to read the raw storage key for an organization. */
export function __getSuppliersListStateStorageKeyForTests(
  organizationId: string
): string {
  return makeKey(organizationId)
}
